// WHAT THE SHELL'S SIDEBAR SHOWS ONCE YOU HAVE PICKED A SITE.
//
// The sidebar used to stop at `/site`. Everything a site owns — its pages, its
// sections, its domains, its files, the document they are composed into — was
// reachable only from the `#site-tabs` strip inside the site, which means the
// shell could not say which site you were in, and the moment you opened a page
// or a section that strip stepped aside (see ./sites/site) and there was
// nothing left naming the site at all but a truncated breadcrumb.
//
// So `Sites` grows a submenu while one is selected: the site, and under it the
// same five screens the tab strip offers, in the same order and under the same
// names.
//
// TWO PLACES NAMING ONE SET OF SCREENS, deliberately. The tabs are how you move
// between a site's screens while you are working in it. This is how you see,
// from the shell, which site you are in and what it holds — which is worth most
// exactly where the tabs are not there. They are free to diverge for that
// reason, and this file is not an attempt to make one of them the source of the
// other; `sites.spec.js` pins the tab strip's five entries, and the test beside
// this file pins these.

/**
 * The entries under `Sites` for the site with this id.
 *
 * Returns the one-item list the sidebar's `submenu` takes, rather than the five
 * entries: what hangs off `Sites` is the site, and the screens hang off that.
 * Three levels is what the sidebar's stylesheet indents for.
 */
export const siteSubmenu = (siteId) => [
  {
    // No `to`, and that is the point of a group: every destination it has is
    // one of the entries below it — the site's own screen is the last of them —
    // so a link here would be a second way to `Configuration`, and a second
    // element carrying the site's id alongside the breadcrumb's crumb.
    icon: "globe",
    label: siteId,
    submenu: [
      // Pages first, because it is where creating a site lands you and what you
      // come back to; Configuration last, because it is the one you visit once.
      // The order is the tab strip's, and changing one without the other is how
      // the two stop reading as the same set of screens.
      { icon: "file-text-o", label: "Pages", to: `/site/${siteId}/page` },
      { icon: "th-large", label: "Sections", to: `/site/${siteId}/section` },
      { icon: "pencil-square-o", label: "Blog", to: `/site/${siteId}/blog` },
      { icon: "link", label: "Domains", to: `/site/${siteId}/domain` },
      // The bytes behind this one are in bucketws, not here — see
      // ./sites/site/files.
      { icon: "folder-open-o", label: "Files", to: `/site/${siteId}/file` },
      // Kept in step with the tab strip by hand — the two are deliberately
      // separate sources and `sidebar.test.js` pins this one.
      { icon: "archive", label: "Route packs", to: `/site/${siteId}/pack` },
      { icon: "plug", label: "Agents", to: `/site/${siteId}/agent` },
      {
        // `exact`, for the reason the matching tab carries it: the site's own
        // path is a prefix of every entry above and would otherwise read as
        // current on all of them.
        icon: "cogs",
        label: "Configuration",
        to: `/site/${siteId}`,
        exact: true,
      },
    ],
  },
];

/**
 * The site you are in, from a `/site/:siteId` match, or null.
 *
 * `new` is not a site. It is the create form, it owns none of the screens
 * above, and it is the one value of `:siteId` that reaches this route without
 * naming a row.
 */
export const siteIdOf = (match) =>
  match && match.params.siteId !== "new" ? match.params.siteId : null;
